import { Component, OnInit, Output, EventEmitter, ElementRef, Input, ViewChild } from '@angular/core';
import { KeyValueId, KeyValueSet, LocalService } from 'src/service/local.service';

@Component({
  selector: 'app-capture-photo',
  templateUrl: './capture-photo.component.html',
  styleUrl: './capture-photo.component.scss'
})
export class CapturePhotoComponent implements OnInit {
  @Input() key: string = '';

  @Output() onClose = new EventEmitter();
  @Output() onCaptured = new EventEmitter();

  @ViewChild('video', { static: true }) video!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;

  stream!: MediaStream;

  cameras: MediaDeviceInfo[] = [];
  currentDeviceId?: string;
  cameraItems: KeyValueId[] = [];

  constructor(public localService: LocalService) {
  }

  ngOnInit(): void {
    this.initCameras();
  }

  async initCameras() {
    // Ask permission first (important for labels)
    const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
    tempStream.getTracks().forEach(t => t.stop());

    const devices = await navigator.mediaDevices.enumerateDevices();
    this.cameras = devices.filter(d => d.kind === 'videoinput');

    // Start first camera by default
    if (this.cameras.length) {
      const lastCameraKey = this.localService.getLastCamera()?.value;
      this.currentDeviceId = lastCameraKey ? this.cameras.find(cam => cam.deviceId === lastCameraKey)?.deviceId : this.cameras[0].deviceId;
      this.startCamera(this.currentDeviceId);
      this.cameraItems = this.cameras.map(cam => ({
        key: cam.deviceId,
        value: `Camera ${this.camIndex(cam)}${cam.label ? ' - ' + cam.label : ''}`
      }));
    }
  }

  startCamera(deviceId: string) {
    this.localService.setLastCamera(deviceId);
    this.stopCamera(false);

    navigator.mediaDevices.getUserMedia({
      video: deviceId ? { deviceId: { exact: deviceId } } : true
    }).then(stream => {
      this.stream = stream;
      this.video.nativeElement.srcObject = stream;
    });
  }

  switchCamera() {
    const item = this.cameras.find(cam => cam.deviceId !== this.currentDeviceId);
    if (item) {
      this.currentDeviceId = item.deviceId;
      this.startCamera(this.currentDeviceId);
    }
  }

  onCameraChange() {
    this.startCamera(this.currentDeviceId);
  }

  stopCamera(isClose: boolean = true) {
    this.stream?.getTracks().forEach(track => track.stop());
    this.video.nativeElement.srcObject = null;
    if (isClose) {
      this.onClose.emit();
    }
  }

  camIndex(cam: MediaDeviceInfo): number {
    return this.cameras.indexOf(cam) + 1;
  }

  capture(): void {
    const video = this.video.nativeElement;
    const canvas = this.canvas.nativeElement;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);

    // 🔥 Base64 image
    const base64Image = canvas.toDataURL('image/png');

    this.onCaptured.emit({
      key: this.key,
      value: base64Image
    } as KeyValueId);

    this.stopCamera();
  }
}
